import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PicAccess as PicAccessData } from "@kkp/api-client";

import {
  getPicAccess,
  restorePicAccess,
  revokePicBrowsers,
  suspendPicAccess,
} from "../../api/companies";
import { Button } from "@kkp/ui";
import { Card, CardBody, CardHeader, CardTitle } from "@kkp/ui";
import { toast } from "@kkp/ui";
import { formatDateTime } from "@kkp/features";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import { Field } from "../../ui/Input";

const PIC_ACCESS_KEY = (companyId: string) => ["pic-access", companyId];

type PendingAction = "revoke" | "suspend" | "restore" | null;

const ACTION_COPY: Record<
  Exclude<PendingAction, null>,
  { title: string; description: string; confirmLabel: string }
> = {
  revoke: {
    title: "Revoke trusted browsers",
    description:
      "Ends every PIC session and clears trusted browsers. The PIC must confirm a new browser before signing in again.",
    confirmLabel: "Revoke",
  },
  suspend: {
    title: "Suspend PIC access",
    description:
      "The PIC will not be able to sign in until access is restored.",
    confirmLabel: "Suspend",
  },
  restore: {
    title: "Restore PIC access",
    description:
      "The PIC can sign in again, but must confirm a trusted browser first.",
    confirmLabel: "Restore",
  },
};

export function PicAccessSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] = React.useState<PendingAction>(null);
  const [reason, setReason] = React.useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: PIC_ACCESS_KEY(companyId),
    queryFn: () => getPicAccess(companyId),
    enabled: Boolean(companyId),
    refetchOnMount: "always",
  });

  const mutation = useMutation({
    mutationFn: async (input: {
      action: Exclude<PendingAction, null>;
      reason: string;
    }) => {
      if (input.action === "revoke") {
        await revokePicBrowsers(companyId, input.reason);
        return;
      }
      if (input.action === "suspend") {
        await suspendPicAccess(companyId, input.reason);
        return;
      }
      await restorePicAccess(companyId, input.reason);
    },
    onSuccess: () => {
      toast.success("PIC access updated.", { title: "Saved" });
      setPendingAction(null);
      setReason("");
      void queryClient.invalidateQueries({
        queryKey: PIC_ACCESS_KEY(companyId),
      });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : "Failed to update PIC access.";
      toast.danger(message, { title: "Error" });
    },
  });

  const closeDialog = () => {
    if (mutation.isPending) return;
    setPendingAction(null);
    setReason("");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">PIC access</CardTitle>
      </CardHeader>

      <CardBody className="grid gap-4">
        {isError ? (
          <div className="text-sm text-[color:var(--kkp-text-muted)]">
            Failed to load PIC access.
          </div>
        ) : null}

        {isLoading ? (
          <div className="text-sm text-[color:var(--kkp-text-muted)]">
            Loading…
          </div>
        ) : null}

        {data ? (
          <PicAccessBody data={data} onAction={setPendingAction} />
        ) : null}
      </CardBody>

      <Dialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent>
          {pendingAction ? (
            <>
              <DialogHeader>
                <DialogTitle>{ACTION_COPY[pendingAction].title}</DialogTitle>
                <DialogDescription>
                  {ACTION_COPY[pendingAction].description}
                </DialogDescription>
              </DialogHeader>

              <Field label="Reason" required>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Required — why is this action being taken?"
                  className="w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:rgba(255,255,255,0.06)] px-3 py-2.5 text-[color:rgba(255,255,255,0.92)] placeholder:text-[color:rgba(255,255,255,0.35)] outline-none transition focus:border-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)] focus:shadow-[0_0_0_3px_rgba(203,72,226,0.18)]"
                />
              </Field>

              <DialogFooter>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={closeDialog}
                  disabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  disabled={!reason.trim() || mutation.isPending}
                  onClick={() =>
                    mutation.mutate({
                      action: pendingAction,
                      reason: reason.trim(),
                    })
                  }
                >
                  {mutation.isPending
                    ? "Saving…"
                    : ACTION_COPY[pendingAction].confirmLabel}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PicAccessBody({
  data,
  onAction,
}: {
  data: PicAccessData;
  onAction: (action: Exclude<PendingAction, null>) => void;
}) {
  return (
    <>
      <div className="text-sm">
        PIC status: <span className="font-bold">{data.picStatus}</span>
      </div>

      <div className="grid gap-2">
        {data.browsers.length === 0 ? (
          <div className="text-sm text-[color:var(--kkp-text-muted)]">
            No trusted browsers.
          </div>
        ) : (
          data.browsers.map((browser, idx) => (
            <div
              key={`${browser.boundAt}-${idx}`}
              className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] px-3 py-2 text-sm"
            >
              <div>Bound: {formatDateTime(browser.boundAt)}</div>
              <div>Last seen: {formatDateTime(browser.lastSeenAt)}</div>
              <div>
                IP: {browser.boundIp} · {browser.boundCountry}
              </div>
              <div className="truncate text-[color:var(--kkp-text-muted)]">
                {browser.boundUserAgent}
              </div>
              {browser.revokedAt ? (
                <div className="text-rose-500">
                  Revoked: {formatDateTime(browser.revokedAt)}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onAction("revoke")}
        >
          Revoke trusted browsers
        </Button>
        {data.picStatus === "disabled" ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onAction("restore")}
          >
            Restore PIC access
          </Button>
        ) : (
          <Button
            variant="danger"
            size="sm"
            onClick={() => onAction("suspend")}
          >
            Suspend PIC access
          </Button>
        )}
      </div>
    </>
  );
}
